import { Box, HStack, IconButton, Spinner, VStack } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { FaCamera, FaSyncAlt, FaTimes } from 'react-icons/fa';
import { Text } from '../../../components/atoms/Text';
import { useTheme } from '../../../contexts/ThemeProvider';

interface CameraCaptureProps {
  onCapture: (dataUrl: string) => void;
  onCancel: () => void;
}

export function CameraCapture({ onCapture, onCancel }: CameraCaptureProps) {
  const { colors } = useTheme();
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [facingMode, setFacingMode] = useState<'environment' | 'user'>('environment');
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    let cancelled = false;

    const stopCurrentStream = () => {
      streamRef.current?.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    };

    setIsLoading(true);
    setErrorMessage('');

    if (!navigator.mediaDevices?.getUserMedia) {
      setIsLoading(false);
      setErrorMessage('Este navegador não permite acesso à câmera. Envie uma foto do documento em vez disso.');
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode }, audio: false })
      .then((stream) => {
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        stopCurrentStream();
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        setIsLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setIsLoading(false);
        setErrorMessage('Não foi possível acessar a câmera. Verifique as permissões do navegador ou envie um arquivo.');
      });

    return () => {
      cancelled = true;
      stopCurrentStream();
    };
  }, [facingMode]);

  const handleCapture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext('2d');
    if (!context) return;

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    onCapture(canvas.toDataURL('image/jpeg', 0.95));
  };

  return (
    <VStack spacing={4} align={'stretch'} width={'100%'}>
      <Box
        position={'relative'}
        width={'100%'}
        maxWidth={'420px'}
        margin={'0 auto'}
        aspectRatio={'4 / 3'}
        borderRadius={'16px'}
        overflow={'hidden'}
        backgroundColor={'#1A202C'}
      >
        {/* playsInline evita tela preta no Safari/iOS ao abrir a câmera */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: isLoading || errorMessage ? 'none' : 'block' }}
        />

        {isLoading && !errorMessage && (
          <VStack position={'absolute'} inset={0} justify={'center'} spacing={3}>
            <Spinner color={'white'} size={'lg'} />
            <Text color={'white'} fontSize={'13px'}>
              Abrindo câmera...
            </Text>
          </VStack>
        )}

        {errorMessage && (
          <VStack position={'absolute'} inset={0} justify={'center'} align={'center'} spacing={2} padding={4}>
            <Text color={'white'} fontSize={'13px'} textAlign={'center'}>
              {errorMessage}
            </Text>
          </VStack>
        )}
      </Box>

      <HStack spacing={3} justify={'center'}>
        <IconButton
          aria-label="Trocar câmera"
          icon={<FaSyncAlt />}
          variant={'outline'}
          borderRadius={'full'}
          onClick={() => setFacingMode((mode) => (mode === 'environment' ? 'user' : 'environment'))}
          isDisabled={isLoading || !!errorMessage}
        />
        <IconButton
          aria-label="Capturar foto"
          icon={<FaCamera />}
          borderRadius={'full'}
          width={'64px'}
          height={'64px'}
          fontSize={'22px'}
          backgroundColor={colors.primaryColor}
          color={'white'}
          _hover={{ backgroundColor: colors.primaryColor }}
          onClick={handleCapture}
          isDisabled={isLoading || !!errorMessage}
        />
        <IconButton aria-label="Cancelar" icon={<FaTimes />} variant={'outline'} borderRadius={'full'} onClick={onCancel} />
      </HStack>
    </VStack>
  );
}
